/**
 * The client runtime (ARCHITECTURE §4.3): one MissionClient (ChronoClient + Outbox +
 * IndicatorController), the stores, the cue engine, and a tiny pub/sub the React tree subscribes
 * to. Framework-independent singletons around the React tree.
 */
import { browserClock } from '@lw/shared/clock/browser';
import { MissionClient, type ClientEvent, type ClientSnapshot, type MissionClientOptions, type SocketLike } from '@lw/client-core';
import { CueEngine } from '../audio/CueEngine.js';
import { AudioEngine } from '../audio/AudioEngine.js';
import { createStores, type Stores } from './stores.js';
import { resolveIdentity, type Identity } from './identity.js';

export interface LogLine {
  at: number;
  text: string;
  cls: '' | 'warn' | 'bad' | 'good';
}

export interface RuntimeState {
  snapshot: ClientSnapshot | null;
  events: ClientEvent[];
  log: LogLine[];
  connected: boolean;
  /** a SHOT pressed on this device that the server has not acknowledged yet (§11.3 UNCONFIRMED) */
  pendingFire: { cmdId: string; linkFlags: string[]; at: number; acknowledgedBanner: boolean } | null;
  /** bumps on every change so React re-renders cheaply */
  version: number;
}

export class Runtime {
  readonly id: Identity;
  readonly stores: Stores;
  readonly client: MissionClient;
  readonly audio: AudioEngine;
  readonly cues: CueEngine;
  readonly clientBuild: string;
  private state: RuntimeState = { snapshot: null, events: [], log: [], connected: false, pendingFire: null, version: 0 };
  private readonly listeners = new Set<() => void>();
  /** perf time of the last layout change that moved the action bar (FireButton ignores taps within 500 ms) */
  lastLayoutChangePerf = 0;

  constructor(opts: { identity?: Identity; clientBuild?: string; baseUrl?: string; openSocket?: MissionClientOptions['openSocket']; fetch?: MissionClientOptions['fetch'] } = {}) {
    this.id = opts.identity ?? resolveIdentity();
    this.clientBuild = opts.clientBuild ?? (import.meta.env['VITE_LW_BUILD'] as string | undefined) ?? 'dev';
    this.stores = createStores(this.id.role);
    const base = opts.baseUrl ?? '';
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    this.audio = new AudioEngine();
    this.client = new MissionClient({
      clock: browserClock,
      role: this.id.role,
      deviceId: this.id.deviceId,
      user: this.id.user,
      bootId: this.id.bootId,
      baseUrl: base,
      wsUrl: base ? base.replace(/^http/, 'ws') + '/api/v1/ws' : `${proto}://${location.host}/api/v1/ws`,
      openSocket: opts.openSocket ?? ((url) => new WebSocket(url) as unknown as SocketLike),
      fetch:
        opts.fetch ??
        (async (url, init) => {
          const r = await fetch(url, { ...init, keepalive: init.method === 'POST' });
          return { ok: r.ok, status: r.status, json: () => r.json() };
        }),
      timeStore: this.stores.time,
      outboxStore: this.stores.outbox,
      missionCache: this.stores.mission,
      random: Math.random,
      clientBuild: this.clientBuild,
      hidden: () => document.hidden,
      online: () => navigator.onLine,
      onEvent: (e) => this.onClientEvent(e),
    });
    this.cues = new CueEngine({ runtime: this, audio: this.audio, clock: browserClock, ledger: this.stores.cueLedger, onLog: (t, cls) => this.log(t, cls) });
    // an OS interruption of the audio context must drop queued cues and refresh the TAP TO ARM badge
    this.audio.onStateChange = (): void => {
      this.cues.onAudioStateChange();
      this.bump();
    };
    this.state.snapshot = this.client.snapshot;
  }

  start(): void {
    void this.stores.ready.then(() => {
      this.client.start();
      this.cues.start();
      this.bump();
    });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.client.resume();
        this.client.indicator.evaluate(); // a backgrounded tab froze its own indicator; refresh at once
        void this.audio.tryResume(); // desktop resumes silently; iOS stays suspended → TAP TO ARM reappears
        this.cues.onVisible();
        this.bump(); // re-render immediately instead of waiting for the next 1 s tick
      } else this.cues.onHidden();
    });
    window.addEventListener('online', () => this.client.resume());
    window.addEventListener('pageshow', () => this.client.resume());
    window.addEventListener('pagehide', () => this.cues.onHidden());
    setInterval(() => this.bump(), 1000); // badges that depend on time (ready age, offline age)
  }

  /** §11.3: stamp and queue the FIRE press; attach the link acknowledgement flags; never gate. */
  pressFire(linkFlags: string[]): void {
    if (this.state.pendingFire) return; // repeat taps reuse the record
    const rec = this.client.send('FIRE', { linkFlags }, { expectedState: 'UAV_READY' });
    this.lastFirePress = { cmdId: rec.cmdId, claimedT0: rec.press.claimedT0 };
    this.state = { ...this.state, pendingFire: { cmdId: rec.cmdId, linkFlags, at: browserClock.perfNow(), acknowledgedBanner: false } };
    this.log(`SHOT pressed${linkFlags.length ? ` (${linkFlags.join(', ')})` : ''}`, 'bad');
    this.bump();
  }

  lastFirePress: { cmdId: string; claimedT0: number | null } | null = null;

  acknowledgeFireBanner(): void {
    if (!this.state.pendingFire) return;
    this.state = { ...this.state, pendingFire: { ...this.state.pendingFire, acknowledgedBanner: true } };
    this.bump();
  }

  // ── pub/sub ──────────────────────────────────────────────────────────────────────────────────
  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  getState(): RuntimeState {
    return this.state;
  }
  private bump(): void {
    this.state = { ...this.state, snapshot: this.client.snapshot, events: this.client.events, connected: this.client.isConnected(), version: this.state.version + 1 };
    for (const l of this.listeners) l();
  }

  log(text: string, cls: LogLine['cls'] = ''): void {
    this.state.log = [{ at: browserClock.wallNow(), text, cls }, ...this.state.log].slice(0, 300);
    this.bump();
  }

  private onClientEvent(e: Parameters<NonNullable<MissionClientOptions['onEvent']>>[0]): void {
    switch (e.kind) {
      case 'state':
        this.cues.onSnapshot(e.snapshot);
        this.bump();
        break;
      case 'mission-event':
        this.cues.onMissionEvents([e.event]);
        this.bump();
        break;
      case 'indicator':
        this.log(`link ${e.event.from ?? '∅'} → ${e.event.to}${e.event.reasons.length ? ` (${e.event.reasons.join(', ')})` : ''}`, e.event.to === 'RED' ? 'bad' : e.event.to === 'GREEN' ? 'good' : 'warn');
        break;
      case 'socket':
        this.bump();
        break;
      case 'chrono':
        if (e.event.type === 'CLOCK_STEP' || e.event.type === 'SYNC_STEP') this.cues.onClockStep(e.event.deltaMs);
        if (e.event.type === 'CLOCK_FREEZE_DETECTED') this.log(`device slept ${(e.event.provisionalPauseMs / 1000).toFixed(1)} s — resyncing`, 'warn');
        if (e.event.type === 'DEGRADED_RESTORED') this.log(`resynced (wall anchor error ${e.event.e.toFixed(0)} ms)`, 'good');
        if (e.event.type === 'CLOCK_STEP') this.log(`clock step ${e.event.deltaMs.toFixed(0)} ms`, 'warn');
        break;
      case 'outbox':
        if (e.event.type === 'OUTBOX_ACKED' && this.state.pendingFire?.cmdId === e.event.cmdId) this.state = { ...this.state, pendingFire: null };
        if (e.event.type === 'client_outbox_dropped' && this.state.pendingFire?.cmdId === e.event.cmdId) this.state = { ...this.state, pendingFire: null };
        if (e.event.type === 'OUTBOX_ATTEMPT_FAILED') this.log(`send failed (${e.event.reason}) — retry in ${(e.event.retryInMs / 1000).toFixed(1)} s`, 'warn');
        if (e.event.type === 'OUTBOX_ACKED') this.log(`${e.event.status === 'APPLIED' ? 'applied' : e.event.status} ${e.event.cmdId.slice(-6)}${e.event.duplicate ? ' (duplicate)' : ''}`, e.event.status === 'APPLIED' ? 'good' : 'warn');
        if (e.event.type === 'client_outbox_dropped') this.log(`queued command dropped: ${e.event.reason}`, 'warn');
        this.bump();
        break;
      case 'resync':
        this.log(e.reason, 'warn');
        break;
    }
  }
}

let runtime: Runtime | null = null;
export function getRuntime(): Runtime {
  if (!runtime) {
    runtime = new Runtime();
    (window as unknown as { __lw: unknown }).__lw = runtime;
  }
  return runtime;
}

/** Create the singleton runtime with a server-provided identity (Phase 5 login flow). */
export function initRuntime(identity: Identity): Runtime {
  if (!runtime) {
    runtime = new Runtime({ identity });
    (window as unknown as { __lw: unknown }).__lw = runtime;
  }
  return runtime;
}
